import { useEffect, useRef, useState } from 'react';
import { getal } from '../i18n.js';

/**
 * Staven per dag met meer reeksen op elkaar (Endpoints: opgeruimd, indicator of compromise, niet opgeruimd). Eén as vanaf
 * 0, 2px tussen de staven en tussen de delen, en een tooltip per dag met elke reeks bij naam. De legenda staat bij de kop.
 *
 * reeksen: [{ sleutel, label, kleur }] van onder naar boven; punten: [{ van, [sleutel]: n }]. Met `perUur` staat het uur erbij
 * (VPN, IPS); met `kop` een eigen kop in de tooltip (Scans: de naam van de scan).
 */
const dag = (d, perUur) => new Date(d).toLocaleString('nl-NL', {
  timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'numeric', ...(perUur ? { hour: '2-digit', minute: '2-digit' } : {}) });

export default function Stapelstaven({ punten, reeksen, hoogte = 200, label, perUur = false, kop = null }) {
  const doos = useRef(null);
  const [W, zetW] = useState(800);
  const [tip, zetTip] = useState(null);
  useEffect(() => {
    const el = doos.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => zetW(Math.max(300, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = hoogte, padL = 40, padB = 22, padT = 12;
  const som = (p) => reeksen.reduce((s, r) => s + (p[r.sleutel] ?? 0), 0);
  const hoogst = Math.max(1, ...punten.map(som));
  const macht = 10 ** Math.floor(Math.log10(hoogst));
  /* Een even top: dan valt het midden van de as op een heel getal (geen "2,5 alerts"). */
  const ruw = Math.max(2, Math.ceil(hoogst / macht) * macht);
  const top = ruw % 2 && macht === 1 ? ruw + 1 : ruw;
  const ih = H - padT - padB;
  const bw = (W - padL) / Math.max(1, punten.length);
  const y = (v) => padT + ih - (v / top) * ih;
  /* Zoveel labels als er passen: een datum met uur is breder dan een datum. */
  const elke = Math.max(1, Math.ceil(punten.length / Math.max(2, Math.floor((W - padL) / (perUur ? 120 : 64)))));

  return (
    <div className="bigchart" ref={doos}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={W} y1={y(top * f)} y2={y(top * f)} stroke="var(--line)" strokeWidth="1" />
            <text x={padL - 6} y={y(top * f) + 4} textAnchor="end" className="cl">{getal(top * f)}</text>
          </g>
        ))}
        {punten.map((p, i) => {
          const x = padL + i * bw + 1;
          const w = Math.max(1, bw - 2);
          let onder = 0;
          return (
            <g key={i}>
              {reeksen.map((r) => {
                const n = p[r.sleutel] ?? 0;
                if (!n) return null;
                const y0 = y(onder) - (onder ? 1 : 0);
                const h = Math.max(1, y(0) - y(n) - (onder ? 1 : 0));
                onder += n;
                return <rect key={r.sleutel} x={x} y={y0 - h} width={w} height={h} fill={r.kleur} rx="2" />;
              })}
              {i % elke === 0 && <text x={i ? x + w / 2 : x} y={H - 5} textAnchor={i ? 'middle' : 'start'} className="cl">{dag(p.van, perUur)}</text>}
              <rect x={padL + i * bw} y={padT} width={bw} height={ih} fill="transparent"
                onMouseEnter={() => zetTip({ p, x: padL + i * bw + bw / 2 })} onMouseLeave={() => zetTip(null)} />
            </g>
          );
        })}
      </svg>
      {tip && (
        <div className="ctip" style={{ left: `${(tip.x / W) * 100}%` }}>
          <b>{kop ? kop(tip.p) : dag(tip.p.van, perUur)}</b>
          {reeksen.slice().reverse().map((r) => <div key={r.sleutel}>{r.label}: {getal(tip.p[r.sleutel] ?? 0)}</div>)}
        </div>
      )}
    </div>
  );
}
